import { newId } from '../lib/db'
import { formatAed } from '../lib/money'
import { emptyExercise, type BodyTag, type Exercise, type ExerciseState, type SetLog } from '../exercise/exercise'
import {
  MAX_DESCRIPTION,
  MAX_TITLE,
  emptyCookbook,
  safeUrl,
  type CookbookState,
  type Recipe,
} from '../cookbook/cookbook'
import { emptySavings, type SavingsState } from '../savings/savings'
import {
  MAX_BONUS_ROUNDS,
  MAX_ROUNDS,
  MAX_SEGMENTS,
  MIN_ROUNDS,
  MIN_SEGMENTS,
  cloneSegments,
  defaultSegments,
  emptyBonusRound,
  emptyGame,
  evenWeights,
  nextColour,
  rebalance,
  unlockSegment,
  weightsAreValid,
  type GameConfig,
  type Segment,
} from '../game/game'
import { emptyHealth, type HealthState, type Measurement, type MeasurementKind } from '../health/health'
import { dateKey, emptyNutrition, validateSplit } from '../nutrition/nutrition'
import type {
  FoodEntry,
  NutritionSettings,
  NutritionState,
  SavedFood,
} from '../nutrition/types'
import { emptyTodos, isExpired as isTodoExpired, type Todo, type TodoState } from '../todo/todo'
import {
  DEFAULT_ALLOWANCE_FILS,
  addMonths,
  buildLog,
  canAfford,
  committedFuture,
  compareMonths,
  computeTimeline,
  currentMonth,
  splitInstallments,
  type CancellationEvent,
  type InstallmentContext,
} from './engine'
import type {
  Adjustment,
  BudgetState,
  BuyResult,
  CheckResult,
  EarnAction,
  Installment,
  Item,
  LedgerEntry,
  LogRow,
  Month,
  Purchase,
  PurchasePlan,
  Settings,
} from './types'

/**
 * Everything for one account, in memory.
 *
 * Mutations here are PURE: they take a snapshot and return a new one. Persistence is the
 * store's job. That split is what lets the same functions run against the server copy,
 * the offline cache, or a test fixture without changing a line.
 */
export interface Snapshot {
  items: Item[]
  purchases: Purchase[]
  installments: Installment[]
  adjustments: Adjustment[]
  ledger: LedgerEntry[]
  actions: EarnAction[]
  settings: Settings
  /** Namespaced per app, so one app's data cannot collide with another's. */
  nutrition: NutritionState
  health: HealthState
  exercise: ExerciseState
  todos: TodoState
  game: GameConfig
  savings: SavingsState
  cookbook: CookbookState
  /** Cross-cutting app preferences. Which sections are hidden lives here. */
  app: { hiddenSections: string[] }
}

export function emptySnapshot(now: Date = new Date()): Snapshot {
  return {
    items: [],
    purchases: [],
    installments: [],
    adjustments: [],
    ledger: [],
    actions: [],
    nutrition: emptyNutrition(),
    health: emptyHealth(),
    exercise: emptyExercise(),
    todos: emptyTodos(),
    game: emptyGame(),
    savings: emptySavings(),
    cookbook: emptyCookbook(),
    app: { hiddenSections: [] },
    settings: {
      monthlyAllowanceFils: DEFAULT_ALLOWANCE_FILS,
      // The first month anchors the timeline: without it, rollover would accumulate from
      // an arbitrary point in the past.
      firstMonth: currentMonth(now),
      earnEnabled: false,
    },
  }
}

/** Tolerates older or partial documents, so a restored backup never renders a blank app. */
export function normaliseSnapshot(raw: unknown, now: Date = new Date()): Snapshot {
  const base = emptySnapshot(now)
  if (!raw || typeof raw !== 'object') return base
  const value = raw as Partial<Snapshot>
  const nutrition = value.nutrition ?? base.nutrition
  return {
    items: value.items ?? [],
    purchases: value.purchases ?? [],
    installments: value.installments ?? [],
    adjustments: value.adjustments ?? [],
    ledger: value.ledger ?? [],
    actions: value.actions ?? [],
    settings: { ...base.settings, ...(value.settings ?? {}) },
    nutrition: {
      settings: { ...base.nutrition.settings, ...(nutrition.settings ?? {}) },
      entries: nutrition.entries ?? [],
      saved: nutrition.saved ?? [],
    },
    health: {
      goals: { ...base.health.goals, ...(value.health?.goals ?? {}) },
      measurements: value.health?.measurements ?? [],
    },
    exercise: {
      exercises: value.exercise?.exercises ?? [],
      logs: value.exercise?.logs ?? [],
    },
    todos: { items: value.todos?.items ?? [] },
    game: normaliseGame(value.game, base.game),
    savings: { ...base.savings, ...(value.savings ?? {}) },
    cookbook: { recipes: value.cookbook?.recipes ?? [] },
    app: { hiddenSections: value.app?.hiddenSections ?? [] },
  }
}

// ----------------------------------------------------------- app preferences

export function setSectionHidden(snapshot: Snapshot, id: string, hidden: boolean): Snapshot {
  const current = snapshot.app.hiddenSections.filter((s) => s !== id)
  return {
    ...snapshot,
    app: { hiddenSections: hidden ? [...current, id] : current },
  }
}

// ---------------------------------------------------------------- cookbook

export function addRecipe(
  snapshot: Snapshot,
  input: { title: string; description: string; url: string; imageUrl: string },
  now: Date = new Date()
): Snapshot {
  const title = input.title.trim()
  if (!title) throw new Error('Give the recipe a title')

  const recipe: Recipe = {
    id: newId(),
    title: title.slice(0, MAX_TITLE),
    description: input.description.trim().slice(0, MAX_DESCRIPTION),
    // An unusable link is dropped rather than stored: half a URL renders as a broken
    // image or a dead tap, and neither is better than nothing.
    url: safeUrl(input.url) ?? '',
    imageUrl: safeUrl(input.imageUrl) ?? '',
    createdAt: now.toISOString(),
  }
  return { ...snapshot, cookbook: { recipes: [...snapshot.cookbook.recipes, recipe] } }
}

export function updateRecipe(
  snapshot: Snapshot,
  id: string,
  patch: Partial<Pick<Recipe, 'title' | 'description' | 'url' | 'imageUrl'>>
): Snapshot {
  return {
    ...snapshot,
    cookbook: {
      recipes: snapshot.cookbook.recipes.map((r) => {
        if (r.id !== id) return r
        const next = { ...r }
        if (patch.title !== undefined) {
          const title = patch.title.trim()
          if (!title) throw new Error('Give the recipe a title')
          next.title = title.slice(0, MAX_TITLE)
        }
        if (patch.description !== undefined) {
          next.description = patch.description.trim().slice(0, MAX_DESCRIPTION)
        }
        if (patch.url !== undefined) next.url = safeUrl(patch.url) ?? ''
        if (patch.imageUrl !== undefined) next.imageUrl = safeUrl(patch.imageUrl) ?? ''
        return next
      }),
    },
  }
}

export function deleteRecipe(snapshot: Snapshot, id: string): Snapshot {
  return {
    ...snapshot,
    cookbook: { recipes: snapshot.cookbook.recipes.filter((r) => r.id !== id) },
  }
}

// ----------------------------------------------------------------- savings

export function setSavingsTarget(snapshot: Snapshot, fils: number): Snapshot {
  if (fils < 0) throw new Error('Enter a target of zero or more')
  return { ...snapshot, savings: { ...snapshot.savings, targetFils: fils } }
}

export function setSavingsCurrent(
  snapshot: Snapshot,
  fils: number,
  now: Date = new Date()
): Snapshot {
  if (fils < 0) throw new Error('Enter an amount of zero or more')
  return {
    ...snapshot,
    savings: { ...snapshot.savings, currentFils: fils, updatedAt: now.toISOString() },
  }
}

// -------------------------------------------------------------------- game

/** Which wheel a configuration change applies to. Every round now has its own. */
export type WheelRef = { kind: 'round'; id: string } | { kind: 'bonus'; id: string }

/** Repairs anything a wheel cannot draw: too few slices, weights that miss 100. */
function fixWheel(segments: Segment[] | undefined, fallback: Segment[]): Segment[] {
  const base = segments && segments.length >= MIN_SEGMENTS ? segments : fallback
  const withDefaults = base.map((s) => ({ ...s, bonusRoundId: s.bonusRoundId ?? null }))
  return weightsAreValid(withDefaults) ? withDefaults : evenWeights(withDefaults)
}

/**
 * Also migrates the older shape, where every round shared one wheel held at the top level.
 * Each round inherits a copy of it, with fresh segment ids so editing one round cannot
 * silently edit another.
 */
function normaliseGame(raw: unknown, base: GameConfig): GameConfig {
  const value = (raw ?? {}) as Partial<GameConfig> & { segments?: Segment[] }

  const bonusRounds = (value.bonusRounds ?? []).map((b) => ({
    ...b,
    segments: fixWheel(b.segments, emptyBonusRound(b.id).segments),
  }))
  const known = new Set(bonusRounds.map((b) => b.id))

  const shared = value.segments
  const rawRounds =
    value.rounds && value.rounds.length >= MIN_ROUNDS ? value.rounds : base.rounds

  const rounds = rawRounds.map((round, i) => {
    const inherited = shared ? cloneSegments(shared, `${round.id}`) : undefined
    const segments = fixWheel(round.segments ?? inherited, defaultSegments(`r${i + 1}`))
    return {
      id: round.id,
      title: round.title,
      // A trigger pointing at a deleted bonus round is dropped rather than left dangling.
      segments: segments.map((s) => ({
        ...s,
        bonusRoundId: s.bonusRoundId && known.has(s.bonusRoundId) ? s.bonusRoundId : null,
      })),
    }
  })

  return { rounds, bonusRounds }
}

function mapWheel(
  snapshot: Snapshot,
  ref: WheelRef,
  fn: (segments: Segment[]) => Segment[]
): Snapshot {
  if (ref.kind === 'round') {
    return {
      ...snapshot,
      game: {
        ...snapshot.game,
        rounds: snapshot.game.rounds.map((r) =>
          r.id === ref.id ? { ...r, segments: fn(r.segments) } : r
        ),
      },
    }
  }
  return {
    ...snapshot,
    game: {
      ...snapshot.game,
      bonusRounds: snapshot.game.bonusRounds.map((b) =>
        b.id === ref.id ? { ...b, segments: fn(b.segments) } : b
      ),
    },
  }
}

export function addSegment(snapshot: Snapshot, ref: WheelRef): Snapshot {
  return mapWheel(snapshot, ref, (segments) => {
    if (segments.length >= MAX_SEGMENTS) return segments
    return evenWeights([
      ...segments,
      {
        id: newId(),
        label: `Option ${segments.length + 1}`,
        colour: nextColour(segments),
        weight: 0,
        bonusRoundId: null,
      },
    ])
  })
}

export function updateSegment(
  snapshot: Snapshot,
  ref: WheelRef,
  id: string,
  patch: { label?: string; bonusRoundId?: string | null }
): Snapshot {
  return mapWheel(snapshot, ref, (segments) =>
    segments.map((s) => (s.id === id ? { ...s, ...patch } : s))
  )
}

/** Changing one percentage moves the others; the set always adds up to 100. */
export function setSegmentWeight(
  snapshot: Snapshot,
  ref: WheelRef,
  id: string,
  weight: number
): Snapshot {
  return mapWheel(snapshot, ref, (segments) => rebalance(segments, id, weight))
}

/** A wheel needs at least two slices to mean anything. */
export function deleteSegment(snapshot: Snapshot, ref: WheelRef, id: string): Snapshot {
  return mapWheel(snapshot, ref, (segments) =>
    segments.length <= MIN_SEGMENTS ? segments : evenWeights(segments.filter((s) => s.id !== id))
  )
}

/** A new round starts as a copy of the last one — usually closer than starting blank. */
export function addRound(snapshot: Snapshot): Snapshot {
  const rounds = snapshot.game.rounds
  if (rounds.length >= MAX_ROUNDS) return snapshot
  const id = newId()
  const previous = rounds[rounds.length - 1]
  return {
    ...snapshot,
    game: {
      ...snapshot.game,
      rounds: [
        ...rounds,
        {
          id,
          title: `Round ${rounds.length + 1}`,
          segments: previous ? cloneSegments(previous.segments, id) : defaultSegments(id),
        },
      ],
    },
  }
}

export function renameRound(snapshot: Snapshot, id: string, title: string): Snapshot {
  return {
    ...snapshot,
    game: {
      ...snapshot.game,
      rounds: snapshot.game.rounds.map((r) => (r.id === id ? { ...r, title } : r)),
    },
  }
}

/** Moves a round one place earlier or later. Bonus rounds are unaffected. */
export function moveRound(snapshot: Snapshot, id: string, direction: -1 | 1): Snapshot {
  const rounds = snapshot.game.rounds
  const index = rounds.findIndex((r) => r.id === id)
  const target = index + direction
  if (index < 0 || target < 0 || target >= rounds.length) return snapshot

  const reordered = [...rounds]
  const [moved] = reordered.splice(index, 1)
  reordered.splice(target, 0, moved)
  return { ...snapshot, game: { ...snapshot.game, rounds: reordered } }
}

export function unlockSegmentWeight(snapshot: Snapshot, ref: WheelRef, id: string): Snapshot {
  return mapWheel(snapshot, ref, (segments) => unlockSegment(segments, id))
}

export function deleteRound(snapshot: Snapshot, id: string): Snapshot {
  if (snapshot.game.rounds.length <= MIN_ROUNDS) return snapshot
  return {
    ...snapshot,
    game: { ...snapshot.game, rounds: snapshot.game.rounds.filter((r) => r.id !== id) },
  }
}

export function addBonusRound(snapshot: Snapshot): Snapshot {
  if (snapshot.game.bonusRounds.length >= MAX_BONUS_ROUNDS) return snapshot
  return {
    ...snapshot,
    game: {
      ...snapshot.game,
      bonusRounds: [...snapshot.game.bonusRounds, emptyBonusRound(newId())],
    },
  }
}

export function renameBonusRound(snapshot: Snapshot, id: string, title: string): Snapshot {
  return {
    ...snapshot,
    game: {
      ...snapshot.game,
      bonusRounds: snapshot.game.bonusRounds.map((b) => (b.id === id ? { ...b, title } : b)),
    },
  }
}

/** Removing a bonus round also clears every option, on any round, that pointed at it. */
export function deleteBonusRound(snapshot: Snapshot, id: string): Snapshot {
  return {
    ...snapshot,
    game: {
      ...snapshot.game,
      bonusRounds: snapshot.game.bonusRounds.filter((b) => b.id !== id),
      rounds: snapshot.game.rounds.map((r) => ({
        ...r,
        segments: r.segments.map((s) => (s.bonusRoundId === id ? { ...s, bonusRoundId: null } : s)),
      })),
    },
  }
}

// ------------------------------------------------------------------ health

export function addMeasurement(
  snapshot: Snapshot,
  kind: MeasurementKind,
  value: number,
  now: Date = new Date()
): Snapshot {
  if (value <= 0) throw new Error('Enter a value above zero')
  const measurement: Measurement = {
    id: newId(),
    kind,
    value,
    date: dateKey(now),
    createdAt: now.toISOString(),
  }
  return {
    ...snapshot,
    health: { ...snapshot.health, measurements: [...snapshot.health.measurements, measurement] },
  }
}

export function deleteMeasurement(snapshot: Snapshot, id: string): Snapshot {
  return {
    ...snapshot,
    health: {
      ...snapshot.health,
      measurements: snapshot.health.measurements.filter((m) => m.id !== id),
    },
  }
}

export function setHealthGoal(
  snapshot: Snapshot,
  kind: MeasurementKind,
  goal: number | null
): Snapshot {
  return {
    ...snapshot,
    health: {
      ...snapshot.health,
      goals:
        kind === 'weight'
          ? { ...snapshot.health.goals, weightKg: goal }
          : { ...snapshot.health.goals, bootyCm: goal },
    },
  }
}

// ---------------------------------------------------------------- exercise

export function addExercise(
  snapshot: Snapshot,
  input: { title: string; description: string; youtubeUrl: string; tag: BodyTag },
  now: Date = new Date()
): Snapshot {
  const title = input.title.trim()
  if (!title) throw new Error('Give the exercise a title')

  const exercise: Exercise = {
    id: newId(),
    title,
    description: input.description.trim(),
    youtubeUrl: input.youtubeUrl.trim() || null,
    tag: input.tag,
    createdAt: now.toISOString(),
  }
  return {
    ...snapshot,
    exercise: { ...snapshot.exercise, exercises: [...snapshot.exercise.exercises, exercise] },
  }
}

export function updateExercise(
  snapshot: Snapshot,
  id: string,
  input: { title: string; description: string; youtubeUrl: string; tag: BodyTag }
): Snapshot {
  const title = input.title.trim()
  if (!title) throw new Error('Give the exercise a title')
  return {
    ...snapshot,
    exercise: {
      ...snapshot.exercise,
      exercises: snapshot.exercise.exercises.map((e) =>
        e.id === id
          ? {
              ...e,
              title,
              description: input.description.trim(),
              youtubeUrl: input.youtubeUrl.trim() || null,
              tag: input.tag,
            }
          : e
      ),
    },
  }
}

/** Removing an exercise takes its history with it — an orphaned log means nothing. */
export function deleteExercise(snapshot: Snapshot, id: string): Snapshot {
  return {
    ...snapshot,
    exercise: {
      exercises: snapshot.exercise.exercises.filter((e) => e.id !== id),
      logs: snapshot.exercise.logs.filter((l) => l.exerciseId !== id),
    },
  }
}

export function logSet(
  snapshot: Snapshot,
  input: { exerciseId: string; weightKg: number; completed: boolean },
  now: Date = new Date()
): Snapshot {
  if (input.weightKg <= 0) throw new Error('Enter a weight above zero')
  const log: SetLog = {
    id: newId(),
    exerciseId: input.exerciseId,
    weightKg: input.weightKg,
    completed: input.completed,
    date: dateKey(now),
    createdAt: now.toISOString(),
  }
  return { ...snapshot, exercise: { ...snapshot.exercise, logs: [...snapshot.exercise.logs, log] } }
}

export function deleteSetLog(snapshot: Snapshot, id: string): Snapshot {
  return {
    ...snapshot,
    exercise: { ...snapshot.exercise, logs: snapshot.exercise.logs.filter((l) => l.id !== id) },
  }
}

// -------------------------------------------------------------------- todos

export function addTodo(
  snapshot: Snapshot,
  input: {
    title: string
    description: string
    date: string
    time: string | null
    repeatDaily?: boolean
  },
  now: Date = new Date()
): Snapshot {
  const title = input.title.trim()
  if (!title) throw new Error('Give the task a title')
  if (!input.date && !input.repeatDaily) throw new Error('Pick a date')

  const todo: Todo = {
    id: newId(),
    title,
    description: input.description.trim(),
    date: input.date || dateKey(now),
    time: input.time || null,
    completedAt: null,
    createdAt: now.toISOString(),
    repeatDaily: Boolean(input.repeatDaily),
    lastCompletedDate: null,
  }
  return { ...snapshot, todos: { items: [...snapshot.todos.items, todo] } }
}

/**
 * Ticking a daily task records the day rather than a timestamp, so it comes back
 * tomorrow instead of clearing itself.
 */
export function toggleTodo(snapshot: Snapshot, id: string, now: Date = new Date()): Snapshot {
  const today = dateKey(now)
  return {
    ...snapshot,
    todos: {
      items: snapshot.todos.items.map((t) => {
        if (t.id !== id) return t
        if (t.repeatDaily) {
          return { ...t, lastCompletedDate: t.lastCompletedDate === today ? null : today }
        }
        return { ...t, completedAt: t.completedAt ? null : now.toISOString() }
      }),
    },
  }
}

export function deleteTodo(snapshot: Snapshot, id: string): Snapshot {
  return { ...snapshot, todos: { items: snapshot.todos.items.filter((t) => t.id !== id) } }
}

/** Ticked items are dropped a day later, so the list stays a list rather than an archive. */
export function purgeCompletedTodos(snapshot: Snapshot, now: Date = new Date()): Snapshot | null {
  const keep = snapshot.todos.items.filter((t) => !isTodoExpired(t, now))
  if (keep.length === snapshot.todos.items.length) return null
  return { ...snapshot, todos: { items: keep } }
}

// --------------------------------------------------------------- nutrition

export function addFoodEntry(
  snapshot: Snapshot,
  input: { label: string; proteinG: number; carbsG: number; fatG: number; alsoSave: boolean },
  now: Date = new Date()
): Snapshot {
  const label = input.label.trim()
  if (!label) throw new Error('Give the food a name')

  const entry: FoodEntry = {
    id: newId(),
    label,
    proteinG: input.proteinG,
    carbsG: input.carbsG,
    fatG: input.fatG,
    date: dateKey(now),
    createdAt: now.toISOString(),
  }

  const next = { ...snapshot, nutrition: { ...snapshot.nutrition, entries: [...snapshot.nutrition.entries, entry] } }
  return input.alsoSave ? saveFood(next, entry, now) : next
}

export function updateFoodEntry(
  snapshot: Snapshot,
  id: string,
  input: { label: string; proteinG: number; carbsG: number; fatG: number }
): Snapshot {
  const label = input.label.trim()
  if (!label) throw new Error('Give the food a name')
  return {
    ...snapshot,
    nutrition: {
      ...snapshot.nutrition,
      entries: snapshot.nutrition.entries.map((e) => (e.id === id ? { ...e, ...input, label } : e)),
    },
  }
}

export function deleteFoodEntry(snapshot: Snapshot, id: string): Snapshot {
  return {
    ...snapshot,
    nutrition: {
      ...snapshot.nutrition,
      entries: snapshot.nutrition.entries.filter((e) => e.id !== id),
    },
  }
}

/** Keeps a food for re-use. Re-saving the same name updates it rather than duplicating. */
export function saveFood(
  snapshot: Snapshot,
  food: { label: string; proteinG: number; carbsG: number; fatG: number },
  now: Date = new Date()
): Snapshot {
  const label = food.label.trim()
  if (!label) return snapshot

  const existing = snapshot.nutrition.saved.find(
    (s) => s.label.toLowerCase() === label.toLowerCase()
  )

  const saved: SavedFood[] = existing
    ? snapshot.nutrition.saved.map((s) =>
        s.id === existing.id
          ? { ...s, proteinG: food.proteinG, carbsG: food.carbsG, fatG: food.fatG }
          : s
      )
    : [
        ...snapshot.nutrition.saved,
        {
          id: newId(),
          label,
          proteinG: food.proteinG,
          carbsG: food.carbsG,
          fatG: food.fatG,
          createdAt: now.toISOString(),
          useCount: 0,
          lastUsedAt: null,
        },
      ]

  return { ...snapshot, nutrition: { ...snapshot.nutrition, saved } }
}

/** One tap: logs the saved food for today and bumps its usage so it sorts to the top. */
export function logSavedFood(
  snapshot: Snapshot,
  savedId: string,
  now: Date = new Date()
): Snapshot {
  const food = snapshot.nutrition.saved.find((s) => s.id === savedId)
  if (!food) return snapshot

  const entry: FoodEntry = {
    id: newId(),
    label: food.label,
    proteinG: food.proteinG,
    carbsG: food.carbsG,
    fatG: food.fatG,
    date: dateKey(now),
    createdAt: now.toISOString(),
  }

  return {
    ...snapshot,
    nutrition: {
      ...snapshot.nutrition,
      entries: [...snapshot.nutrition.entries, entry],
      saved: snapshot.nutrition.saved.map((s) =>
        s.id === savedId
          ? { ...s, useCount: s.useCount + 1, lastUsedAt: now.toISOString() }
          : s
      ),
    },
  }
}

export function deleteSavedFood(snapshot: Snapshot, id: string): Snapshot {
  return {
    ...snapshot,
    nutrition: {
      ...snapshot.nutrition,
      saved: snapshot.nutrition.saved.filter((s) => s.id !== id),
    },
  }
}

export function setNutritionSettings(
  snapshot: Snapshot,
  settings: NutritionSettings
): Snapshot {
  const problem = validateSplit(settings)
  if (problem) throw new Error(problem)
  return { ...snapshot, nutrition: { ...snapshot.nutrition, settings } }
}

/** Most-used first, then most recent — the food you reach for should be at the top. */
export function sortedSavedFoods(snapshot: Snapshot): SavedFood[] {
  return [...snapshot.nutrition.saved].sort(
    (a, b) =>
      b.useCount - a.useCount ||
      (b.lastUsedAt ?? b.createdAt).localeCompare(a.lastUsedAt ?? a.createdAt)
  )
}

// ------------------------------------------------------------------ derive

const allowanceOf = (snapshot: Snapshot) => () => snapshot.settings.monthlyAllowanceFils

function adjustmentMap(snapshot: Snapshot): Map<Month, number> {
  const map = new Map<Month, number>()
  for (const a of snapshot.adjustments) {
    map.set(a.month, (map.get(a.month) ?? 0) + a.amountFils)
  }
  return map
}

function firstMonthOf(snapshot: Snapshot, month: Month): Month {
  return compareMonths(snapshot.settings.firstMonth, month) <= 0
    ? snapshot.settings.firstMonth
    : month
}

function installmentContexts(snapshot: Snapshot): InstallmentContext[] {
  const purchases = new Map(snapshot.purchases.map((p) => [p.id, p]))
  return snapshot.installments.flatMap((installment) => {
    const purchase = purchases.get(installment.purchaseId)
    if (!purchase) return []
    return [
      {
        installment,
        itemTitle: purchase.titleSnapshot,
        brand: purchase.brandSnapshot,
        plan: purchase.plan,
        totalCount: purchase.plan === 'split4' ? 4 : 1,
        purchaseTimestamp: purchase.createdAt,
      },
    ]
  })
}

export function deriveBudget(snapshot: Snapshot, now: Date = new Date()): BudgetState {
  const month = currentMonth(now)
  const full = computeTimeline({
    firstMonth: firstMonthOf(snapshot, month),
    untilMonth: addMonths(month, 6),
    installments: snapshot.installments,
    adjustments: adjustmentMap(snapshot),
    allowanceFor: allowanceOf(snapshot),
  })

  const history = full.filter((r) => compareMonths(r.month, month) <= 0)
  const projection = full.filter((r) => compareMonths(r.month, month) > 0)
  const current = history.at(-1)

  return {
    currentMonth: month,
    monthlyAllowanceFils: snapshot.settings.monthlyAllowanceFils,
    availableNowFils: current?.closingFils ?? 0,
    rolledOverFils: current?.openingFils ?? 0,
    spentThisMonthFils: current?.chargesFils ?? 0,
    committedFutureFils: snapshot.installments
      .filter((i) => compareMonths(i.dueMonth, month) > 0)
      .reduce((sum, i) => sum + i.amountFils, 0),
    months: history,
    projection,
    committed: committedFuture(installmentContexts(snapshot), month),
  }
}

export function deriveLog(snapshot: Snapshot, now: Date = new Date()): LogRow[] {
  const month = currentMonth(now)
  return buildLog({
    firstMonth: firstMonthOf(snapshot, month),
    untilMonth: month,
    contexts: installmentContexts(snapshot).filter(
      (c) => compareMonths(c.installment.dueMonth, month) <= 0
    ),
    adjustments: snapshot.adjustments,
    cancellations: snapshot.ledger
      .filter((l) => l.type === 'PURCHASE_CANCELLED')
      .map<CancellationEvent>((l) => ({
        id: l.id,
        month: l.month,
        releasedFils: l.amountFils,
        itemTitle: l.note.split('"')[1] ?? 'a purchase',
        timestamp: l.ts,
      })),
    allowanceFor: allowanceOf(snapshot),
  })
}

export interface ItemView extends Item {
  effectivePriceFils: number
  isOverridden: boolean
  purchase: {
    id: string
    plan: PurchasePlan
    totalFils: number
    paidCount: number
    totalCount: number
  } | null
}

export function deriveItems(snapshot: Snapshot, now: Date = new Date()): ItemView[] {
  const month = currentMonth(now)
  const activeByItem = new Map(
    snapshot.purchases.filter((p) => p.itemId && p.status === 'active').map((p) => [p.itemId!, p])
  )

  return snapshot.items
    .map<ItemView>((item) => {
      const purchase = activeByItem.get(item.id)
      const rows = purchase ? snapshot.installments.filter((i) => i.purchaseId === purchase.id) : []
      return {
        ...item,
        effectivePriceFils: item.overridePriceFils ?? item.priceFils,
        isOverridden: item.overridePriceFils !== null && item.overridePriceFils !== item.priceFils,
        purchase: purchase
          ? {
              id: purchase.id,
              plan: purchase.plan,
              totalFils: purchase.totalFils,
              paidCount: rows.filter((i) => compareMonths(i.dueMonth, month) <= 0).length,
              totalCount: purchase.plan === 'split4' ? 4 : 1,
            }
          : null,
      }
    })
    .sort(
      (a, b) =>
        Number(a.status === 'purchased') - Number(b.status === 'purchased') ||
        a.title.localeCompare(b.title)
    )
}

/** Actions the user can still act on: unexpired, and not already done unless repeatable. */
export function deriveActions(snapshot: Snapshot, now: Date = new Date()): EarnAction[] {
  const iso = now.toISOString()
  return snapshot.actions
    .filter((a) => (a.repeatable || a.completedAt === null) && (!a.expiresAt || a.expiresAt > iso))
    .sort((a, b) => (a.expiresAt ?? '9999').localeCompare(b.expiresAt ?? '9999'))
}

export function checkAffordability(
  snapshot: Snapshot,
  itemId: string,
  plan: PurchasePlan,
  now: Date = new Date()
): CheckResult | null {
  const item = snapshot.items.find((i) => i.id === itemId)
  if (!item) return null
  const price = item.overridePriceFils ?? item.priceFils
  if (price <= 0) return null

  const month = currentMonth(now)
  return canAfford({
    firstMonth: firstMonthOf(snapshot, month),
    currentMonth: month,
    installments: snapshot.installments,
    adjustments: adjustmentMap(snapshot),
    allowanceFor: allowanceOf(snapshot),
    candidate: splitInstallments(price, plan, month, 'preview', (seq) => `preview-${seq}`),
    plan,
    priceFils: price,
  })
}

// --------------------------------------------------------------- mutations

export function addItem(
  snapshot: Snapshot,
  draft: {
    title: string
    brand?: string | null
    imageUrl?: string | null
    sourceUrl?: string | null
    priceFils: number
    currencyRaw?: string | null
  },
  now: Date = new Date()
): Snapshot {
  if (draft.priceFils <= 0) throw new Error('Price must be positive')
  const item: Item = {
    id: newId(),
    title: draft.title.trim(),
    brand: draft.brand?.trim() || null,
    imageUrl: draft.imageUrl?.trim() || null,
    sourceUrl: draft.sourceUrl?.trim() || null,
    priceFils: draft.priceFils,
    overridePriceFils: null,
    currencyRaw: draft.currencyRaw ?? null,
    status: 'wishlist',
    createdAt: now.toISOString(),
    purchasedAt: null,
  }
  return { ...snapshot, items: [...snapshot.items, item] }
}

export function setOverridePrice(
  snapshot: Snapshot,
  itemId: string,
  fils: number | null
): Snapshot {
  if (fils !== null && fils <= 0) throw new Error('Price must be positive')
  return {
    ...snapshot,
    items: snapshot.items.map((i) => (i.id === itemId ? { ...i, overridePriceFils: fils } : i)),
  }
}

/**
 * The only path that spends money.
 *
 * Re-reads the price and re-runs the affordability rule rather than trusting whatever the
 * check sheet was showing, so a sheet left open while the price changed cannot slip a
 * purchase through.
 */
export function buy(
  snapshot: Snapshot,
  itemId: string,
  plan: PurchasePlan,
  expectedPriceFils: number,
  force = false,
  now: Date = new Date()
): { snapshot: Snapshot; result: BuyResult } {
  const item = snapshot.items.find((i) => i.id === itemId)
  if (!item) return { snapshot, result: { kind: 'invalid', reason: 'Item no longer exists' } }
  if (item.status === 'purchased') return { snapshot, result: { kind: 'already-purchased' } }

  const price = item.overridePriceFils ?? item.priceFils
  if (price <= 0) return { snapshot, result: { kind: 'invalid', reason: 'Price must be positive' } }
  if (price !== expectedPriceFils) {
    return { snapshot, result: { kind: 'price-changed', actualPriceFils: price } }
  }

  const month = currentMonth(now)
  const purchaseId = newId()
  const candidate = splitInstallments(price, plan, month, purchaseId, () => newId())

  const check = canAfford({
    firstMonth: firstMonthOf(snapshot, month),
    currentMonth: month,
    installments: snapshot.installments,
    adjustments: adjustmentMap(snapshot),
    allowanceFor: allowanceOf(snapshot),
    candidate,
    plan,
    priceFils: price,
  })

  if (!check.ok && !force) {
    return {
      snapshot,
      result: {
        kind: 'not-affordable',
        shortfallFils: check.shortfallFils,
        month: check.shortfallMonth ?? month,
      },
    }
  }

  const timestamp = now.toISOString()
  const purchase: Purchase = {
    id: purchaseId,
    itemId: item.id,
    titleSnapshot: item.title,
    brandSnapshot: item.brand,
    totalFils: price,
    plan,
    status: 'active',
    createdAt: timestamp,
    cancelledAt: null,
  }

  return {
    snapshot: {
      ...snapshot,
      purchases: [...snapshot.purchases, purchase],
      installments: [...snapshot.installments, ...candidate],
      items: snapshot.items.map((i) =>
        i.id === item.id ? { ...i, status: 'purchased' as const, purchasedAt: timestamp } : i
      ),
    },
    result: { kind: 'success', purchaseId, plan, itemTitle: item.title },
  }
}

/**
 * Cancels a purchase and gives the whole price back.
 *
 * Installments due after this month are dropped so they never charge; whatever was
 * already charged is credited to the current month as a refund. Past months keep the
 * balances they actually had — history is not rewritten — but the full amount returns to
 * what can be spent today.
 */
function cancelPurchase(snapshot: Snapshot, purchase: Purchase, now: Date): Snapshot {
  const month = currentMonth(now)
  const timestamp = now.toISOString()
  const rows = snapshot.installments.filter((i) => i.purchaseId === purchase.id)
  const alreadyCharged = rows
    .filter((i) => compareMonths(i.dueMonth, month) <= 0)
    .reduce((sum, i) => sum + i.amountFils, 0)
  const released = rows
    .filter((i) => compareMonths(i.dueMonth, month) > 0)
    .reduce((sum, i) => sum + i.amountFils, 0)

  const adjustments: Adjustment[] =
    alreadyCharged > 0
      ? [
          ...snapshot.adjustments,
          {
            id: newId(),
            month,
            amountFils: alreadyCharged,
            note: purchase.titleSnapshot,
            createdAt: timestamp,
            source: 'refund' as const,
          },
        ]
      : snapshot.adjustments

  const ledgerEntry: LedgerEntry = {
    id: newId(),
    ts: timestamp,
    month,
    type: 'PURCHASE_CANCELLED',
    amountFils: alreadyCharged + released,
    itemId: purchase.itemId,
    purchaseId: purchase.id,
    note: `Cancelled "${purchase.titleSnapshot}" — refunded ${formatAed(alreadyCharged + released)}`,
  }

  return {
    ...snapshot,
    installments: snapshot.installments.filter(
      (i) => i.purchaseId !== purchase.id || compareMonths(i.dueMonth, month) <= 0
    ),
    purchases: snapshot.purchases.map((p) =>
      p.id === purchase.id ? { ...p, status: 'cancelled' as const, cancelledAt: timestamp } : p
    ),
    adjustments,
    ledger: [...snapshot.ledger, ledgerEntry],
  }
}

export function deleteItem(snapshot: Snapshot, itemId: string, now: Date = new Date()): Snapshot {
  const item = snapshot.items.find((i) => i.id === itemId)
  if (!item) return snapshot

  let next = snapshot
  const purchase = snapshot.purchases.find((p) => p.itemId === itemId && p.status === 'active')
  if (purchase) {
    next = cancelPurchase(next, purchase, now)
    // The purchase outlives the item so the log stays readable.
    next = {
      ...next,
      purchases: next.purchases.map((p) => (p.id === purchase.id ? { ...p, itemId: null } : p)),
    }
  }

  return { ...next, items: next.items.filter((i) => i.id !== itemId) }
}

export function addAdjustment(
  snapshot: Snapshot,
  amountFils: number,
  note: string,
  now: Date = new Date()
): Snapshot {
  if (amountFils === 0) return snapshot
  return {
    ...snapshot,
    adjustments: [
      ...snapshot.adjustments,
      {
        id: newId(),
        month: currentMonth(now),
        amountFils,
        note: note.trim() || null,
        createdAt: now.toISOString(),
        source: 'manual',
      },
    ],
  }
}

export function setMonthlyAllowance(snapshot: Snapshot, fils: number): Snapshot {
  if (fils <= 0) throw new Error('Allowance must be positive')
  return { ...snapshot, settings: { ...snapshot.settings, monthlyAllowanceFils: fils } }
}

export function setEarnEnabled(snapshot: Snapshot, enabled: boolean): Snapshot {
  return { ...snapshot, settings: { ...snapshot.settings, earnEnabled: enabled } }
}

// ------------------------------------------------------------ earn actions

export function addEarnAction(
  snapshot: Snapshot,
  input: {
    title: string
    description: string
    rewardFils: number
    expiresInDays: number | null
    repeatable: boolean
  },
  now: Date = new Date()
): Snapshot {
  if (!input.title.trim()) throw new Error('Give the action a title')
  if (input.rewardFils <= 0) throw new Error('The reward must be positive')

  const expiresAt =
    input.repeatable || !input.expiresInDays || input.expiresInDays <= 0
      ? null
      : new Date(now.getTime() + input.expiresInDays * 86_400_000).toISOString()

  const action: EarnAction = {
    id: newId(),
    title: input.title.trim(),
    description: input.description.trim(),
    rewardFils: input.rewardFils,
    expiresAt,
    completedAt: null,
    createdAt: now.toISOString(),
    repeatable: input.repeatable,
    completedCount: 0,
    lastCompletedAt: null,
  }

  return { ...snapshot, actions: [...snapshot.actions, action] }
}

/**
 * Completing pays the reward into the current month as an earned adjustment, so it lands
 * in the log without ever touching the purchased-items list.
 */
export function completeEarnAction(
  snapshot: Snapshot,
  id: string,
  now: Date = new Date()
): { snapshot: Snapshot; reward: number | null } {
  const action = snapshot.actions.find((a) => a.id === id)
  if (!action) return { snapshot, reward: null }
  if (action.completedAt && !action.repeatable) return { snapshot, reward: null }
  if (action.expiresAt && action.expiresAt <= now.toISOString()) return { snapshot, reward: null }

  const timestamp = now.toISOString()
  return {
    snapshot: {
      ...snapshot,
      actions: snapshot.actions.map((a) =>
        a.id === id
          ? {
              ...a,
              completedAt: a.repeatable ? null : timestamp,
              lastCompletedAt: timestamp,
              completedCount: a.completedCount + 1,
            }
          : a
      ),
      adjustments: [
        ...snapshot.adjustments,
        {
          id: newId(),
          month: currentMonth(now),
          amountFils: action.rewardFils,
          note: action.title,
          createdAt: timestamp,
          source: 'earned' as const,
        },
      ],
    },
    reward: action.rewardFils,
  }
}

export function deleteEarnAction(snapshot: Snapshot, id: string): Snapshot {
  return { ...snapshot, actions: snapshot.actions.filter((a) => a.id !== id) }
}

/** Expired-and-never-completed actions are dropped, not archived: they never happened. */
export function purgeExpiredActions(snapshot: Snapshot, now: Date = new Date()): Snapshot | null {
  const iso = now.toISOString()
  const keep = snapshot.actions.filter(
    (a) => a.completedAt !== null || a.expiresAt === null || a.expiresAt > iso
  )
  if (keep.length === snapshot.actions.length) return null
  return { ...snapshot, actions: keep }
}

// ---------------------------------------------------------------- transfer

export function exportJson(snapshot: Snapshot): string {
  return JSON.stringify({ schema: 2, exportedAt: new Date().toISOString(), ...snapshot }, null, 2)
}

export function importJson(json: string, now: Date = new Date()): Snapshot {
  const data = JSON.parse(json)
  // Android backups nest their actions under a different key.
  if (data.earnActions && !data.actions) data.actions = data.earnActions
  return normaliseSnapshot(data, now)
}
